import { useEffect, useMemo, useState } from 'react'

const AJAX_ACTION = 'nova_theme_product_add_to_cart'

function htmlMarkup(html) {
  return { __html: html || '' }
}

function updateHeaderCartCount(count) {
  document.querySelectorAll('span.site-navbar__cart-count, span.site-sticky-cart__count').forEach((element) => {
    element.textContent = String(count || 0)
  })
}

function getSubmitButton(form, submitter) {
  if (submitter && submitter.matches?.('.single_add_to_cart_button, button[type="submit"], input[type="submit"]')) {
    return submitter
  }

  return form.querySelector('.single_add_to_cart_button, button[type="submit"], input[type="submit"]')
}

function appendSubmitterData(body, submitter) {
  if (!submitter?.name || body.has(submitter.name)) {
    return
  }

  body.append(submitter.name, submitter.value || '')
}

function ensureProductId(body, form, submitter) {
  if (body.get('product_id')) {
    return
  }

  const submitterProductId = submitter?.name === 'add-to-cart' ? submitter.value : ''
  const addToCartId = body.get('add-to-cart') || submitterProductId

  if (addToCartId) {
    body.append('product_id', addToCartId)
    return
  }

  const productInput = form.querySelector('[name="product_id"], [name="add-to-cart"]')
  if (productInput?.value) {
    body.append('product_id', productInput.value)
  }
}

function validateVariation(form, labels) {
  if (!form.classList.contains('variations_form')) {
    return ''
  }

  const variationId = form.querySelector('[name="variation_id"]')?.value
  return variationId && variationId !== '0' ? '' : labels.selectOptions || 'Veuillez choisir les options du produit.'
}

function triggerWooCartEvents(data, button) {
  const fragments = data.fragments || {}
  const cartHash = data.cartHash || ''

  if (window.jQuery) {
    window.jQuery(document.body).trigger('added_to_cart', [fragments, cartHash, window.jQuery(button)])
    window.jQuery(document.body).trigger('wc_fragment_refresh')
  }

  window.dispatchEvent(
    new CustomEvent('nova:cart-updated', {
      detail: {
        cart: data.cart || {},
        fragments,
        cartHash,
      },
    }),
  )
}

export function ProductAddToCart({ form, config }) {
  const labels = useMemo(() => config.labels || {}, [config.labels])
  const [notice, setNotice] = useState(null)
  const [noticesHtml, setNoticesHtml] = useState('')
  const [isBusy, setIsBusy] = useState(false)

  useEffect(() => {
    if (!form || !config.ajaxUrl) {
      return undefined
    }

    const handleSubmit = async (event) => {
      const button = getSubmitButton(form, event.submitter)

      if (!button) {
        return
      }

      if (button.disabled || button.classList.contains('disabled')) {
        event.preventDefault()

        const validationError = validateVariation(form, labels)
        if (validationError) {
          setNoticesHtml('')
          setNotice({ type: 'error', message: validationError })
        }

        return
      }

      event.preventDefault()

      const validationError = validateVariation(form, labels)
      if (validationError) {
        setNoticesHtml('')
        setNotice({ type: 'error', message: validationError })
        return
      }

      const body = new window.FormData(form)
      appendSubmitterData(body, event.submitter || button)
      ensureProductId(body, form, event.submitter || button)
      body.append('action', AJAX_ACTION)
      body.append('nonce', config.ajaxNonce || '')

      const originalText = button.textContent
      const wasDisabled = button.disabled

      setIsBusy(true)
      setNotice(null)
      setNoticesHtml('')
      button.disabled = true
      button.classList.add('nova-product-add-to-cart-is-loading')
      button.textContent = labels.adding || 'Ajout...'

      try {
        const response = await window.fetch(config.ajaxUrl, {
          method: 'POST',
          credentials: 'same-origin',
          body,
        })
        const result = await response.json()

        if (!result.success) {
          const errorMessage = result.data?.message || labels.genericError || "Impossible d'ajouter cet article au panier."
          setNoticesHtml(result.data?.noticesHtml || '')
          setNotice({ type: 'error', message: errorMessage })
          return
        }

        const data = result.data || {}
        const cartCount = data.cart?.itemsCount ?? data.cartCount

        updateHeaderCartCount(cartCount)
        triggerWooCartEvents(data, button)
        setNotice({ type: 'success', message: data.message || labels.added || 'Article ajoute au panier.' })

        if (config.openCartSidebar) {
          window.dispatchEvent(new CustomEvent('nova:open-cart-sidebar', { detail: { source: 'product-page' } }))
        }
      } catch (requestError) {
        setNotice({ type: 'error', message: labels.networkError || labels.genericError || requestError.message })
      } finally {
        setIsBusy(false)
        button.disabled = wasDisabled
        button.classList.remove('nova-product-add-to-cart-is-loading')
        button.textContent = originalText
      }
    }

    form.addEventListener('submit', handleSubmit)
    return () => form.removeEventListener('submit', handleSubmit)
  }, [config.ajaxNonce, config.ajaxUrl, config.openCartSidebar, form, labels])

  if (!notice && !noticesHtml && !isBusy) {
    return null
  }

  return (
    <div className="nova-product-add-to-cart-status" aria-live="polite">
      {noticesHtml ? <div dangerouslySetInnerHTML={htmlMarkup(noticesHtml)} /> : null}
      {notice ? (
        <div className={`nova-product-add-to-cart-status__message is-${notice.type}`}>{notice.message}</div>
      ) : null}
    </div>
  )
}
